import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { FaCalendarAlt, FaTimes, FaPlus } from 'react-icons/fa';
import './GlobalFiltersBar.scss';
import { Grid } from '@mui/material';
import { useGlobalFilters } from '../hooks/useGlobalFilters';
import { useRegionsData } from '../hooks/useRegions';
import { useSessionsData } from '../hooks/useSessions';
import SessionsDropDown from './SessionsDropDown';
import { session_job_types_by_routes, isDynamicSessionJobType} from '../utils/sessions';
import { useLocation } from 'react-router-dom';
import { toJS } from 'mobx';
import TimelineEventsPanel from './TimelineEventsPanel';


const GlobalFiltersBar = observer(({ onFiltersChange }) => {
  const filtersStore = useGlobalFilters();
  const { regions, isLoading: isGroupsLoading, hasFetched: hasFetchedGroups } = useRegionsData();
  const { sessions, isLoading: isSessionsLoading } = useSessionsData(false);
  const location = useLocation();

  useEffect(() => {
    if (!hasFetchedGroups) return;
    const visibleIds = new Set(regions.flatMap(region =>
      (region.groups || []).map(group => String(group.group_id))
    ));
    const selected = filtersStore.selectedGroups;
    const visibleSelection = selected.filter(id => visibleIds.has(String(id)));
    if (visibleSelection.length !== selected.length) {
      filtersStore.setSelectedGroups(visibleSelection);
    }
  }, [hasFetchedGroups, regions, filtersStore, filtersStore.selectedGroups]);

  /*
  const isSessionRoute = Object.keys(session_job_types_by_routes).includes(location.pathname);
  const session_job_type = isSessionRoute
    ? session_job_types_by_routes[location.pathname]
    : undefined;
  console.log("session_job_type",toJS(sessions[session_job_type]))
  */
  const isSessionRoute = isDynamicSessionJobType(location.pathname);
  const session_job_type = isSessionRoute
  const selectedSession = useMemo(() => {
    if (!isSessionRoute || !session_job_type) return undefined;
  
    const sessionList = toJS(sessions[session_job_type] || []);
    const selectedId = toJS(filtersStore.selectedSession[session_job_type]);
  
    console.log("Все session_id:", sessionList.map(s => s.session_id));
    console.log("Текущее выбранное session_id:", selectedId);
  
    // 1️⃣ Пытаемся найти выбранную сессию
    let found = sessionList.find(
      s => String(s.session_id) === String(selectedId)
    );
  
    // 2️⃣ Если выбранная отсутствует — ищем первую "валидную"
    if (!found) {
      found = sessionList.find(
        s => s.start_date && s.end_date && s.group_id
      );
  
      if (found) {
        console.log("Fallback → выбрана первая валидная сессия:", found.session_id);
  
        // 💾 Устанавливаем fallback-сессию в стор
        filtersStore.setSelectedSession(session_job_type, found.session_id);
      } else {
        console.log("Нет подходящей сессии с полями start_date, end_date, group_id");
      }
    }
  
    console.log("found:", found);
    return found || undefined;
  }, [
    isSessionRoute,
    session_job_type,
    filtersStore.selectedSession[session_job_type],
    sessions[session_job_type]?.length,
  ]);
  
  
  

  // Обработка изменения регионов
  const handleRegionChange = (groups, checked) => { 
    if (checked) {
      groups.forEach(groupId => filtersStore.addGroup(groupId));
    } else {
      groups.forEach(groupId => filtersStore.removeGroup(groupId));
    }
  };

  // Удаление тега группы
  const handleRemoveTag = (groupId) => {
    filtersStore.removeGroup(groupId);
  };

  // Получение названия группы по ID
  const getGroupTitle = (groupId) => {
    for (const region of regions) {
      const group = region.groups?.find(g => g.group_id === groupId);
      if (group) return group.title;
    }
    return groupId;
  };

  // Обработка изменения групп
  const handleGroupChange = (e) => {
    const groupId = e.target.value;
    const checked = e.target.checked;
    
    if (checked) {
      filtersStore.addGroup(groupId);
    } else {
      filtersStore.removeGroup(groupId);
    }
  };

  // Обработка изменения фильтров
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    switch (name) {
      case 'minLikes':
        filtersStore.setMinLikes(value);
        break;
      case 'minViews':
        filtersStore.setMinViews(value);
        break;
      case 'minComments':
        filtersStore.setMinComments(value);
        break;
      case 'startDate':
        filtersStore.setStartDate(value);
        break;
      case 'endDate':
        filtersStore.setEndDate(value);
        break;
      default:
        break;
    }
  };

  // Открыть/закрыть выпадающий список
  const toggleDropdown = () => {
    filtersStore.setShowDropdown(!filtersStore.showDropdown);
  };

  // Закрыть выпадающий список
  const closeDropdown = () => {
    filtersStore.setShowDropdown(false);
  };

  // Обработчик клика вне выпадающего списка
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filtersStore.showDropdown && !event.target.closest('.dropdown-container')) {
        filtersStore.setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filtersStore.showDropdown]);

  // Уведомление родителя об изменениях фильтров
  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange(filtersStore.filters);
    }
  }, [filtersStore.filters, onFiltersChange]);


  
  useEffect(() => {    
    if (!selectedSession) return;
  
    const { start_date, end_date, group_id } = selectedSession;
    console.log(start_date, end_date, group_id);
  
    const today = new Date().toISOString().split('T')[0]; // формат YYYY-MM-DD
  
    // Корректируем start_date и end_date, если они позже сегодняшнего дня
    const validStartDate =
      start_date && start_date > today ? today : start_date;
    const validEndDate =
      end_date && end_date > today ? today : end_date;
  
    if (validStartDate && filtersStore.startDate !== validStartDate) {
      filtersStore.setStartDate(validStartDate);
    }
  
    if (validEndDate && filtersStore.endDate !== validEndDate) {
      filtersStore.setEndDate(validEndDate);
    }
  
    // Проверяем и записываем выбранные группы
    if (group_id) {
      // Приводим к массиву на случай, если group_id — строка или число
      const newGroups = Array.isArray(group_id) ? group_id : [group_id];
  
      if (JSON.stringify(filtersStore.selectedGroups) !== JSON.stringify(newGroups)) {
        filtersStore.setSelectedGroups(newGroups);
      }
    }
  }, [selectedSession]);
  
  return (
    <div className="filters-bar-style__container">
      {isSessionRoute ? (
        <Grid item xs={12} md={3}>
        <div className="filters-bar-style">
            <div className="filters-bar-style__date-input">
            {/* ComboBox для выбора сессий */}
            <SessionsDropDown session_job_type={session_job_type}/>
            </div>
            <TimelineEventsPanel />
          </div>
        </Grid>
      ):(
        <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <div className="filters-bar-style">
            <div className="filters-bar-style__date-input">
              <FaCalendarAlt style={{ marginRight: 4 }} />
              <input
                type="date"
                name="startDate"
                value={filtersStore.startDate}
                min={filtersStore.DATE_MIN}
                max={filtersStore.endDate || filtersStore.DATE_MAX}  // 👈 теперь нельзя выбрать позже endDate
                onChange={handleChange}
                className="filters-bar-style__date-input__input"
              />
              <span style={{ color: '#888', fontWeight: 400 }}>—</span>
              <input
                type="date"
                name="endDate"
                value={filtersStore.endDate}
                min={filtersStore.startDate || filtersStore.DATE_MIN}  // 👈 теперь нельзя выбрать раньше startDate
                max={filtersStore.DATE_MAX}
                onChange={handleChange}
                className="filters-bar-style__date-input__input"
              />

            </div>
          </div>
        </Grid>


        <Grid item xs={12} md={9}>
          <div className="filters-bar-style__selected-groups">
            {filtersStore.selectedGroups.map(groupId => (
              <span key={groupId} className="tag-style">
                {getGroupTitle(groupId)}
                <FaTimes className="close-icon-style" onClick={() => handleRemoveTag(groupId)} />
              </span>
            ))}
            <button className="tag-btn-style" onClick={toggleDropdown} type="button">
              <FaPlus style={{ marginRight: 8 }} />
              Выбрать сообщества...
            </button>
            <TimelineEventsPanel />
          </div>

          <div className="dropdown-container" style={{ position: 'relative' }}>
            {filtersStore.showDropdown && (
              <div className="groups-dropdown-menu">
                {!isGroupsLoading ? (
                  regions.map((regionData) => (
                    <div key={regionData.city}>
                      <div className='groups-dropdown-menu__region-header'>
                        <input
                          type="checkbox"
                          onChange={(e) => handleRegionChange(regionData.groups.map(g => g.group_id), e.target.checked)}
                          checked={regionData.groups.every((group) =>
                            filtersStore.selectedGroups.includes(String(group.group_id))
                          )}
                          className='groups-dropdown-menu__region-header__checkbox'
                        />
                        {regionData.city}
                      </div>
                      {regionData.groups.map((group) => (
                        <div key={group.id} className='groups-dropdown-menu__group-item'>
                          <input
                            type="checkbox"
                            value={group.group_id}
                            onChange={handleGroupChange}
                            checked={filtersStore.selectedGroups.includes(String(group.group_id))}
                            className='groups-dropdown-menu__group-item__checkbox'
                          />
                          <span>{group.title}</span>
                        </div>
                      ))}
                    </div>
                  ))
                ) : (
                  <div className='groups-dropdown-menu__loading'>
                    Загрузка групп...
                  </div>
                )}
              </div>
            )}
          </div>
        </Grid>
      </Grid>
      )}
    </div>
  );
});

export default GlobalFiltersBar;
